import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Search, Calendar, Trophy, Users, Pencil, Trash2, AlertTriangle, Share2, Play, X as XIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AGE_CATEGORIES = ['U11', 'U13', 'U15', 'U17', 'U19', 'Open'];

const MatchHistory = () => {
  const { token, user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpponent, setSearchOpponent] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingMatch, setEditingMatch] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sharingHighlight, setSharingHighlight] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchMatches();
  }, [searchOpponent, dateFrom, dateTo]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchOpponent) params.append('opponent', searchOpponent);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/matches/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (match) => {
    setEditingMatch({
      ...match,
      match_date: format(new Date(match.match_date), 'yyyy-MM-dd'),
      _matchType: match.match_type || 'singles',
      _isPractice: match.is_practice || false,
      _bestOfThree: match.best_of_three || false,
      _ageCategory: match.age_category || '',
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const em = editingMatch;
      const payload = {
        match_type: em._matchType,
        is_practice: em._isPractice,
        age_category: em._ageCategory || null,
        tournament_name: em._isPractice ? null : (em.tournament_name || null),
        opponent_name: em.opponent_name,
        teammate_name: em._matchType === 'doubles' ? (em.teammate_name || null) : null,
        opponent2_name: em._matchType === 'doubles' ? (em.opponent2_name || null) : null,
        best_of_three: em._bestOfThree,
        match_date: em.match_date,
        video_url: em.video_url || null,
        notes: em.notes || null
      };

      if (em._bestOfThree) {
        payload.game1_user_score = parseInt(em.game1_user_score) || 0;
        payload.game1_opponent_score = parseInt(em.game1_opponent_score) || 0;
        payload.game2_user_score = parseInt(em.game2_user_score) || 0;
        payload.game2_opponent_score = parseInt(em.game2_opponent_score) || 0;
        payload.game3_user_score = em.game3_user_score ? parseInt(em.game3_user_score) : null;
        payload.game3_opponent_score = em.game3_opponent_score ? parseInt(em.game3_opponent_score) : null;
        payload.user_score = parseInt(em.game1_user_score) || 0;
        payload.opponent_score = parseInt(em.game1_opponent_score) || 0;
      } else {
        payload.user_score = parseInt(em.user_score) || 0;
        payload.opponent_score = parseInt(em.opponent_score) || 0;
      }

      await axios.put(`${API}/matches/${em.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Match updated', description: 'Your changes have been saved.' });
      setEditingMatch(null);
      fetchMatches();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update match', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/matches/${deleteConfirm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Match deleted', description: 'The match has been removed.' });
      setDeleteConfirm(null);
      fetchMatches();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete match', variant: 'destructive' });
    }
  };

  const clearFilters = () => {
    setSearchOpponent('');
    setDateFrom('');
    setDateTo('');
  };

  const shareHighlight = async (match) => {
    setSharingHighlight(match.id);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');

      const grad = ctx.createLinearGradient(0, 0, 600, 400);
      grad.addColorStop(0, match.result === 'win' ? '#064e3b' : '#7f1d1d');
      grad.addColorStop(1, match.result === 'win' ? '#10b981' : '#ef4444');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 600, 400);

      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      for (let i = 0; i < 600; i += 40) ctx.fillRect(i, 0, 1, 400);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('BADMINTON STATS', 24, 36);

      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(match.result === 'win' ? 'VICTORY' : 'DEFEAT', 300, 100);

      ctx.font = 'bold 72px Arial';
      ctx.fillText(`${match.user_score} - ${match.opponent_score}`, 300, 200);

      ctx.font = '24px Arial';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fillText(`${user?.username || 'You'} vs ${match.opponent_name}`, 300, 260);

      ctx.font = '16px Arial';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText(format(new Date(match.match_date), 'MMMM dd, yyyy'), 300, 300);
      if (match.tournament_name) ctx.fillText(match.tournament_name, 300, 330);
      if (match.is_practice) ctx.fillText('Practice Match', 300, 360);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const formData = new FormData();
      formData.append('file', new File([blob], 'match-highlight.png', { type: 'image/png' }));
      
      const uploadRes = await axios.post(`${API}/posts/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      const caption = match.result === 'win' 
        ? `Match Highlight: ${match.user_score}-${match.opponent_score} vs ${match.opponent_name}! Another W in the bag!` 
        : `Tough match vs ${match.opponent_name}: ${match.user_score}-${match.opponent_score}. We go again!`;

      await axios.post(`${API}/posts/`, {
        media_type: 'image',
        media_url: uploadRes.data.media_url,
        caption,
        is_match_highlight: true,
        tagged_users: []
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast({ title: 'Highlight shared!', description: 'Your match highlight has been posted to your feed.' });
    } catch (error) {
      console.error('Failed to share highlight:', error);
      toast({ title: 'Failed to share', variant: 'destructive' });
    } finally {
      setSharingHighlight(null);
    }
  };

  const updateEdit = (field, value) => setEditingMatch(prev => ({ ...prev, [field]: value }));

  if (loading && matches.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="match-history">
      {/* Search Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder="Search opponent..."
              value={searchOpponent}
              onChange={(e) => setSearchOpponent(e.target.value)}
              data-testid="search-opponent-input"
            />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from-input" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to-input" />
            {(searchOpponent || dateFrom || dateTo) && (
              <Button variant="outline" onClick={clearFilters}>Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Matches List */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Match History</span>
            <Badge variant="secondary">{matches.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {matches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No matches found</p>
            </div>
          ) : (
            <div className="divide-y">
              {matches.map((match) => (
                <div key={match.id} className="p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-xl font-bold shrink-0 ${match.result === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                        {match.result === 'win' ? 'W' : 'L'}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium text-sm truncate">vs {match.opponent_name}</span>
                          {match.is_practice && <Badge variant="outline" className="text-[10px] px-1 py-0">Practice</Badge>}
                          {match.match_type === 'doubles' && <Badge variant="outline" className="text-[10px] px-1 py-0">Doubles</Badge>}
                          {match.best_of_three && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">Bo3</Badge>}
                          {match.video_url && (
                            <button
                              onClick={() => setPlayingVideo(playingVideo === match.id ? null : match.id)}
                              className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                              data-testid={`play-video-${match.id}`}
                            >
                              <Play className="h-2.5 w-2.5" /> Video
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {format(new Date(match.match_date), 'MMM dd, yyyy')}
                          {match.tournament_name && ` · ${match.tournament_name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {match.best_of_three ? (
                        <div className="flex items-center gap-1" data-testid={`sets-${match.id}`}>
                          <span className={`text-xs font-semibold px-1 py-0.5 rounded ${match.game1_user_score > match.game1_opponent_score ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {match.game1_user_score}-{match.game1_opponent_score}
                          </span>
                          {match.game2_user_score != null && match.game2_opponent_score != null && (
                            <span className={`text-xs font-semibold px-1 py-0.5 rounded ${match.game2_user_score > match.game2_opponent_score ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {match.game2_user_score}-{match.game2_opponent_score}
                            </span>
                          )}
                          {match.game3_user_score != null && match.game3_opponent_score != null && (
                            <span className={`text-xs font-semibold px-1 py-0.5 rounded ${match.game3_user_score > match.game3_opponent_score ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {match.game3_user_score}-{match.game3_opponent_score}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="font-bold text-sm">{match.user_score}-{match.opponent_score}</span>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => shareHighlight(match)} disabled={sharingHighlight === match.id} data-testid={`share-highlight-${match.id}`}>
                        {sharingHighlight === match.id ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" /> : <Share2 className="h-3.5 w-3.5 text-blue-500" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(match)} data-testid={`edit-match-${match.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteConfirm(match.id)} data-testid={`delete-match-${match.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  {/* Inline Video Player */}
                  {playingVideo === match.id && match.video_url && (
                    <div className="mt-2 relative rounded-lg overflow-hidden bg-black" data-testid={`video-player-${match.id}`}>
                      <button
                        onClick={() => setPlayingVideo(null)}
                        className="absolute top-2 right-2 z-10 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                      <video
                        src={match.video_url}
                        controls
                        autoPlay
                        className="w-full max-h-48 rounded-lg"
                        playsInline
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Edit Dialog */}
      <Dialog open={!!editingMatch} onOpenChange={() => setEditingMatch(null)}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Edit Match</DialogTitle>
          </DialogHeader>
          {editingMatch && (
            <div className="space-y-3">
              {/* Type + Date */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Match Type</Label>
                  <Select value={editingMatch._matchType} onValueChange={(v) => updateEdit('_matchType', v)}>
                    <SelectTrigger className="h-9" data-testid="edit-match-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="singles">Singles</SelectItem>
                      <SelectItem value="doubles">Doubles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Match Date</Label>
                  <Input type="date" value={editingMatch.match_date} onChange={(e) => updateEdit('match_date', e.target.value)} className="h-9" data-testid="edit-match-date" />
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                  <Label className="text-xs cursor-pointer">Practice</Label>
                  <Switch checked={editingMatch._isPractice} onCheckedChange={(v) => updateEdit('_isPractice', v)} data-testid="edit-practice-toggle" />
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                  <Label className="text-xs cursor-pointer">Best of 3</Label>
                  <Switch checked={editingMatch._bestOfThree} onCheckedChange={(v) => updateEdit('_bestOfThree', v)} data-testid="edit-bo3-toggle" />
                </div>
              </div>

              {/* Age + Tournament */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Age Category</Label>
                  <Select value={editingMatch._ageCategory} onValueChange={(v) => updateEdit('_ageCategory', v)}>
                    <SelectTrigger className="h-9" data-testid="edit-age-category">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-xs ${editingMatch._isPractice ? 'text-gray-400' : ''}`}>Tournament</Label>
                  <Input
                    value={editingMatch.tournament_name || ''}
                    onChange={(e) => updateEdit('tournament_name', e.target.value)}
                    disabled={editingMatch._isPractice}
                    className={`h-9 ${editingMatch._isPractice ? 'bg-gray-100' : ''}`}
                    data-testid="edit-tournament"
                  />
                </div>
              </div>

              {/* Opponent */}
              <div>
                <Label className="text-xs">Opponent Name</Label>
                <Input value={editingMatch.opponent_name} onChange={(e) => updateEdit('opponent_name', e.target.value)} className="h-9" data-testid="edit-opponent" />
              </div>

              {/* Doubles */}
              {editingMatch._matchType === 'doubles' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Teammate</Label>
                    <Input value={editingMatch.teammate_name || ''} onChange={(e) => updateEdit('teammate_name', e.target.value)} className="h-9" data-testid="edit-teammate" />
                  </div>
                  <div>
                    <Label className="text-xs">2nd Opponent</Label>
                    <Input value={editingMatch.opponent2_name || ''} onChange={(e) => updateEdit('opponent2_name', e.target.value)} className="h-9" data-testid="edit-opponent2" />
                  </div>
                </div>
              )}

              {/* Scores */}
              {editingMatch._bestOfThree ? (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Game 1</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min="0" placeholder="You" value={editingMatch.game1_user_score ?? ''} onChange={(e) => updateEdit('game1_user_score', e.target.value)} className="h-9" data-testid="edit-g1-you" />
                      <Input type="number" min="0" placeholder="Opp" value={editingMatch.game1_opponent_score ?? ''} onChange={(e) => updateEdit('game1_opponent_score', e.target.value)} className="h-9" data-testid="edit-g1-opp" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Game 2</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min="0" placeholder="You" value={editingMatch.game2_user_score ?? ''} onChange={(e) => updateEdit('game2_user_score', e.target.value)} className="h-9" data-testid="edit-g2-you" />
                      <Input type="number" min="0" placeholder="Opp" value={editingMatch.game2_opponent_score ?? ''} onChange={(e) => updateEdit('game2_opponent_score', e.target.value)} className="h-9" data-testid="edit-g2-opp" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Game 3 (optional)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min="0" placeholder="You" value={editingMatch.game3_user_score ?? ''} onChange={(e) => updateEdit('game3_user_score', e.target.value)} className="h-9" data-testid="edit-g3-you" />
                      <Input type="number" min="0" placeholder="Opp" value={editingMatch.game3_opponent_score ?? ''} onChange={(e) => updateEdit('game3_opponent_score', e.target.value)} className="h-9" data-testid="edit-g3-opp" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Your Score</Label>
                    <Input type="number" min="0" value={editingMatch.user_score} onChange={(e) => updateEdit('user_score', e.target.value)} className="h-9" data-testid="edit-your-score" />
                  </div>
                  <div>
                    <Label className="text-xs">Opponent Score</Label>
                    <Input type="number" min="0" value={editingMatch.opponent_score} onChange={(e) => updateEdit('opponent_score', e.target.value)} className="h-9" data-testid="edit-opp-score" />
                  </div>
                </div>
              )}

              {/* Video URL */}
              <div>
                <Label className="text-xs">Video URL</Label>
                <Input value={editingMatch.video_url || ''} onChange={(e) => updateEdit('video_url', e.target.value)} className="h-9" data-testid="edit-video-url" />
              </div>

              {/* Notes */}
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={editingMatch.notes || ''} onChange={(e) => updateEdit('notes', e.target.value)} rows={2} data-testid="edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setEditingMatch(null)} className="h-9">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="h-9">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Match
            </DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this match? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MatchHistory;
